import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../lib/api';
import EventCard from '../components/EventCard';
import MapView from '../components/MapView';
import { Button } from '../components/ui/button';
import { MapTrifold, Trophy, Sparkle, ArrowRight, MagnifyingGlass, PaperPlaneTilt, ShoppingBagOpen } from '@phosphor-icons/react';
import Seo from '../components/Seo';
export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [topPayouts, setTopPayouts] = useState([]);
  const [recent, setRecent] = useState([]);
  const [allEvents, setAllEvents] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get('/events/featured'),
      api.get('/events/top-payouts'),
      api.get('/events/recent'),
      api.get('/events?limit=200'),
    ]).then(([f, t, r, a]) => {
      setFeatured(f.data);
      setTopPayouts(t.data);
      setRecent(r.data);
      setAllEvents(a.data);
    }).catch(() => {});
  }, []);

  return (
    <div>
      <Seo
        path="/"
        title={null}
        description="Discover Native American & First Nations pow wows across Turtle Island. Search by state, tribe, or dance category. Interactive map, event details, trip planner. Find. Plan. Celebrate. Honor."
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'WebSite',
          name: 'Pow Wow Finder',
          url: 'https://powwowfinder.com',
          description: 'Discover Native American & First Nations pow wows across Turtle Island.',
          potentialAction: {
            '@type': 'SearchAction',
            target: 'https://powwowfinder.com/events?q={search_term_string}',
            'query-input': 'required name=search_term_string',
          },
        }}
      />
      {/* HERO */}
      <section className="relative min-h-[88vh] overflow-hidden bg-navy pt-12 pb-20">
        {/* Decorative pattern background */}
        <div className="absolute inset-0 pattern-aztec opacity-30" />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-[1fr_1.1fr] gap-8 lg:gap-12 items-center">
          {/* LEFT: Pow wow dancer artwork — cropped to show just the dancer */}
          <div className="relative order-2 lg:order-1 fade-up">
            <div className="absolute -inset-6 bg-gradient-to-br from-turquoise/20 via-crimson/10 to-gold/10 blur-3xl -z-10 rounded-full" />
            <div className="relative aspect-[3/4] sm:aspect-[4/5] overflow-hidden rounded-3xl">
              <img
                src="/dancer-banner.png"
                alt="Pow Wow dancer in regalia"
                className="absolute inset-0 w-[280%] h-full object-cover drop-shadow-2xl"
                style={{ objectPosition: '0% center' }}
              />
              {/* Soft gradient mask edges so it blends into the dark navy bg */}
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-transparent to-navy pointer-events-none" />
              <div className="absolute inset-0 bg-gradient-to-b from-transparent to-navy/40 pointer-events-none" />
            </div>
          </div>

          {/* RIGHT: Headline + CTAs */}
          <div className="order-1 lg:order-2 text-foreground fade-up">
            <p className="label text-turquoise mb-4 flex items-center gap-2">
              <span className="w-8 h-px bg-turquoise" /> Find · Plan · Celebrate · Honor
            </p>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-[1.02] mb-6 text-shadow-hero">
              Your journey.<br />
              Our <span className="text-turquoise italic">culture.</span><br />
              One community.
            </h1>
            <p className="text-lg text-foreground/85 leading-relaxed mb-8 max-w-xl">
              {allEvents.length || '127'}+ real pow wows scraped from across Turtle Island — search by tribe, payout, dance category, or let our trip planner build your road trip.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to="/events" data-testid="hero-browse-btn">
                <Button size="lg" className="bg-turquoise text-navy hover:bg-turquoise/90 rounded-full px-7 h-12 font-bold shadow-xl shadow-turquoise/20 glow-pulse">
                  Browse all events <ArrowRight size={18} className="ml-2" />
                </Button>
              </Link>
              <Link to="/plan" data-testid="hero-plan-btn">
                <Button size="lg" variant="outline" className="rounded-full h-12 px-7 border-gold/50 text-gold hover:bg-gold hover:text-navy bg-transparent font-semibold">
                  <Sparkle size={18} weight="duotone" className="mr-2" /> Trip Planner
                </Button>
              </Link>
              <Link to="/submit" data-testid="hero-submit-btn">
                <Button size="lg" variant="outline" className="rounded-full h-12 px-7 border-crimson/60 text-crimson hover:bg-crimson hover:text-bone bg-transparent font-semibold">
                  <PaperPlaneTilt size={18} weight="duotone" className="mr-2" /> Submit a pow wow
                </Button>
              </Link>
              <Link to="/map" data-testid="hero-map-btn">
                <Button size="lg" variant="outline" className="rounded-full h-12 px-7 border-foreground/30 text-foreground hover:bg-foreground/10 hover:text-foreground bg-transparent">
                  <MapTrifold size={18} weight="duotone" className="mr-2" /> Map view
                </Button>
              </Link>
            </div>
            <div className="mt-12 flex items-center gap-8 text-foreground/70 text-sm">
              <div><span className="font-display text-3xl text-crimson block leading-none">{allEvents.length || '127'}</span><span className="text-xs uppercase tracking-widest">events</span></div>
              <div><span className="font-display text-3xl text-turquoise block leading-none">200+</span><span className="text-xs uppercase tracking-widest">tribes</span></div>
              <div><span className="font-display text-3xl text-gold block leading-none">$2M+</span><span className="text-xs uppercase tracking-widest">payouts</span></div>
            </div>
          </div>
        </div>
      </section>

      {/* Native-made clothing partner — placed above Marquee */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-4" data-testid="partner-section">
        <a
          href="https://www.buyn8v.com"
          target="_blank"
          rel="noopener noreferrer"
          className="group block relative overflow-hidden rounded-3xl border border-crimson/30 bg-card hover:border-crimson transition-all shadow-2xl shadow-crimson/5"
          data-testid="partner-buyn8v"
        >
          <div className="grid lg:grid-cols-2 min-h-[480px]">
            {/* Image */}
            <div
              className="relative min-h-[420px] lg:min-h-full bg-cover bg-center bg-no-repeat"
              style={{ backgroundImage: 'url(https://customer-assets.emergentagent.com/job_cultural-compass-15/artifacts/ygnng4pu_463900174_8826388997413078_8157162689851384532_n.jpg)' }}
              role="img"
              aria-label="Native-made streetwear by N8V Couture — models in Definitely Native hoodies"
            >
              {/* Fallback IMG element in case background-image is blocked by ad-blockers etc. */}
              <img
                src="https://customer-assets.emergentagent.com/job_cultural-compass-15/artifacts/ygnng4pu_463900174_8826388997413078_8157162689851384532_n.jpg"
                alt=""
                loading="eager"
                decoding="async"
                className="absolute inset-0 w-full h-full object-cover object-center opacity-0"
                onLoad={e => { e.currentTarget.style.opacity = '1'; e.currentTarget.parentElement.style.backgroundImage = 'none'; }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-navy/60 via-transparent to-transparent lg:bg-gradient-to-r lg:from-transparent lg:to-card/40 pointer-events-none" />
            </div>

            {/* Copy */}
            <div className="relative p-8 sm:p-12 lg:p-16 flex flex-col justify-center">
              <div className="absolute inset-0 pattern-aztec opacity-30 pointer-events-none" />
              <div className="relative">
                <span className="inline-flex items-center gap-2 bg-crimson/15 text-crimson text-[10px] uppercase tracking-[0.25em] font-bold px-3 py-1.5 rounded-full mb-5">
                  <ShoppingBagOpen size={12} weight="bold" /> Native-owned · Featured partner
                </span>
                <h2 className="font-display text-4xl sm:text-5xl tracking-tight mb-4">
                  Shop <span className="text-crimson">Native-Made</span> Clothing
                </h2>
                <p className="text-muted-foreground text-lg leading-relaxed mb-6 max-w-md">
                  Streetwear, hoodies, and statement pieces designed and crafted by Native artists.
                  Rep the culture wherever the trail takes you.
                </p>
                <div className="inline-flex items-center gap-3 bg-crimson text-bone font-semibold px-7 py-3.5 rounded-full group-hover:bg-crimson/90 group-hover:gap-4 transition-all">
                  Visit buyn8v.com
                  <ArrowRight size={18} weight="bold" />
                </div>
                <p className="text-xs text-muted-foreground mt-5 font-mono">www.buyn8v.com</p>
              </div>
            </div>
          </div>
        </a>
      </section>

      {/* FEATURED */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="flex items-end justify-between mb-10">
          <div>
            <p className="label text-terracotta mb-2">Featured</p>
            <h2 className="font-display text-4xl tracking-tight">Marquee pow wows this season</h2>
          </div>
          <Link to="/events" className="hidden sm:inline-flex items-center text-sm font-semibold hover:text-terracotta">
            See all <ArrowRight size={16} className="ml-1" />
          </Link>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {featured.map((e) => <EventCard key={e.event_id} event={e} />)}
        </div>
      </section>

      {/* PAYOUTS */}
      <section className="bg-forest text-bone py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between mb-10">
            <div>
              <p className="label text-sand mb-2"><Trophy size={14} weight="duotone" className="inline mr-1" /> Largest payouts</p>
              <h2 className="font-display text-4xl tracking-tight">Top prize pools this year</h2>
            </div>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {topPayouts.map((e, i) => (
              <Link key={e.event_id} to={`/events/${e.event_id}`} className="group bg-bone/5 hover:bg-bone/10 backdrop-blur border border-bone/10 rounded-2xl p-6 transition-all" data-testid={`payout-card-${e.event_id}`}>
                <div className="flex items-start justify-between mb-3">
                  <span className="label text-sand">#{i+1}</span>
                  <span className="font-display text-3xl text-sand font-semibold">${(e.total_prize_pool/1000).toFixed(0)}k</span>
                </div>
                <h3 className="font-display text-xl mb-1 group-hover:text-sand">{e.name}</h3>
                <p className="text-bone/60 text-sm">{e.tribe} · {e.city}, {e.state}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* RECENT */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="flex items-end justify-between mb-10 flex-wrap gap-3">
          <div>
            <p className="label text-terracotta mb-2"><Sparkle size={14} weight="duotone" className="inline mr-1" /> Just added</p>
            <h2 className="font-display text-4xl tracking-tight">Recently announced</h2>
            <p className="text-sm text-muted-foreground mt-2">Fresh pow wows — updated automatically every 24 hours from powwows.com.</p>
          </div>
          <Link to="/events" className="hidden sm:inline-flex items-center text-sm font-semibold hover:text-terracotta">
            See all <ArrowRight size={16} className="ml-1" />
          </Link>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="recent-events">
          {recent.slice(0, 6).map((e) => <EventCard key={e.event_id} event={e} />)}
        </div>
      </section>

      {/* TRIBES */}
      <section className="bg-muted/40 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="label text-terracotta mb-2 text-center">Featured nations</p>
          <h2 className="font-display text-4xl tracking-tight text-center mb-12">Hosting nations across Turtle Island</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {['Diné (Navajo)','Lakota','Apsáalooke','Cherokee','Mohawk','Cree','Ojibwe','Apache','Comanche','Blackfeet','Pequot','Spokane'].map(t => (
              <div key={t} className="bg-card border border-border rounded-xl p-4 text-center hover:border-terracotta hover:-translate-y-0.5 transition-all">
                <p className="font-display text-sm">{t}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 text-center">
        <p className="label text-terracotta mb-4">Organizer?</p>
        <h2 className="font-display text-4xl sm:text-5xl tracking-tight mb-4">Get your pow wow on the map.</h2>
        <p className="text-muted-foreground max-w-xl mx-auto mb-8">
          Free submissions, full event pages with payout tables, head staff, and lodging — moderated by our team within 24 hours.
        </p>
        <Link to="/submit" data-testid="cta-submit-btn">
          <Button size="lg" className="bg-terracotta hover:bg-terracotta/90 text-white rounded-full px-8 h-12">
            Submit your event
          </Button>
        </Link>
      </section>
    </div>
  );
}
